/**
 * E1 Documenten — types, DTO's en Zod-schemas voor de generieke documenten-laag.
 *
 * Modelteren de Prisma-enums Document* als string-tuples + label-records voor
 * NL-talige UI. DTOs zijn een afspiegeling van Prisma-rijen + computed velden
 * (latestVersionNr, latestUploadedAt, …).
 */

import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'

// ─── Enums (matching Prisma enums) ────────────────────────────────────────────

export const DOCUMENT_CATEGORIE = [
  'CONTRACT',
  'CERTIFICAAT',
  'ID_BEWIJS',
  'EVALUATIE',
  'OVEREENKOMST',
  'HANDLEIDING',
  'PROTOCOL',
  'OVERIG',
] as const
export type DocumentCategorie = (typeof DOCUMENT_CATEGORIE)[number]

export const DOCUMENT_SCOPE = [
  'EMPLOYEE',
  'CLIENT',
  'WERKBON',
  'BEDRIJFSBREED',
  // Fase 2 (gap-fix) — gespiegeld met Prisma-enum DocumentScope. KWALITEIT-
  // bijlagen (foto's bij bevinding-taken) komen in fase 6; COMMUNICATIE-
  // bijlagen (PDF in bericht) komen in fase 6; VOERTUIG-bijlagen (kenteken-
  // bewijs, schadeformulier) komen in fase 7.
  'KWALITEIT',
  'COMMUNICATIE',
  'VOERTUIG',
  // Fase 8 — bijlagen bij een ongeval-/incidentregistratie.
  'ONGEVAL',
  // Batch-7 — documenten per acquisitie-lead resp. kandidaat. Backoffice-only
  // (ACQUISITIE-/RECRUITMENT-rechten); de portal-scope-check weigert beide.
  'LEAD',
  'KANDIDAAT',
  // FO-051 (batch-16) — automatisch bewaarde data-exports (Excel via de export-
  // engine). Zichtbaarheid erft de module-permissie van de bron-export; nooit
  // bedrijfsbreed, nooit portal/klant.
  'EXPORT',
  // FO-314 (batch-34) — contract/voorwaarden/opzegbevestiging bij een materieel-
  // abonnement (backoffice-only, ASSETS; nooit portal/klant).
  'ABONNEMENT',
  // batch-55 bruikleen-docs (FO-421) — bijlage bij een uniek middel
  // (VoorraadUniekItem: gereedschap, telefoon, kar, koffer). Backoffice-only,
  // nooit portal/klant; de koppeling loopt via DocumentKoppeling.
  'VOORRAAD_UNIEK_ITEM',
] as const
export type DocumentScope = (typeof DOCUMENT_SCOPE)[number]

export const DOCUMENT_AUDIT_ACTIE = [
  'GEUPLOAD',
  'NIEUWE_VERSIE',
  'GEDOWNLOAD',
  'VERWIJDERD',
  'TOEGANG_GEWIJZIGD',
  'CATEGORIE_GEWIJZIGD',
  // FO-064 (batch-19) — bibliotheek-acties.
  'GEOPEND',
  'RECHTEN_GEWIJZIGD',
  'ZICHTBAARHEID_GEWIJZIGD',
  'GEDEELD_MEDEWERKER',
  'GEDEELD_KLANT',
  'KOPPELING_GEWIJZIGD',
  // FO-077 (batch-20) — bibliotheek-labels (handmatig omlabelen).
  'LABEL_TOEGEVOEGD',
  'LABEL_VERWIJDERD',
  // batch-62 briefpapier (FO-459) - het briefpapier van een gegenereerde versie.
  'BRIEFPAPIER_VASTGELEGD',
] as const
export type DocumentAuditActie = (typeof DOCUMENT_AUDIT_ACTIE)[number]

// ─── FO-064 (batch-19) Bibliotheek-enums ──────────────────────────────────────

/** Documenttype (FO-064 sectie 5) — stuurt de virtuele mappen + standaardrechten. */
export const DOCUMENT_TYPE = [
  'FACTUUR',
  'INKOOPFACTUUR',
  'CREDITFACTUUR',
  'WERKBON',
  'CERTIFICAAT',
  'DIPLOMA',
  'SKILLMATRIX',
  'ARBEIDSOVEREENKOMST',
  'BRUIKLEENOVEREENKOMST',
  'WERKGEVERSVERKLARING',
  'LOONSTROOK',
  'JAAROPGAVE',
  'DECLARATIEBON',
  'OFFERTE',
  'KLANTCONTRACT',
  'VEILIGHEIDSDOCUMENT',
  'OPLEIDINGSDOCUMENT',
  'HANDLEIDING',
  'TEMPLATE',
  'ALGEMEEN_DOCUMENT',
  // batch-37 schadeformulier (FO-326) — gegenereerd Europees aanrijdingsformulier.
  'SCHADEFORMULIER',
  // 2026-07-21 — identiteits-/pasdocumenten in het medewerker-dossier.
  'ID_KAART',
  'PASPOORT',
  'RIJBEWIJS',
  'BANKPAS',
  // Voertuigdocumenten (upload-dropdown op de voertuig-Documenten-tab).
  'GROENE_KAART',
  'INZETVERKLARING',
  // batch-65 apk-rapport (FO-478) - APK-keuringsrapport (voertuig verplicht).
  'APK_RAPPORT',
  // batch-67 campagnes (FO-501) - verklaring geen privegebruik / uitsluitend
  // zakelijk gebruik, gegenereerd uit de bijtellingskeuze (dossier + voertuig).
  'VERKLARING_PRIVEGEBRUIK',
  // batch-68 werkplaatsfacturen (FO-341) - de factuur-PDF van een garage als
  // voertuigdocument; verwijst naar de opslag van de inkoopfactuur-bijlage.
  'WERKPLAATSFACTUUR',
  // batch-71 workflow-sjablonen (FO-533 §D9) - de documenten van een voertuigverkoop plus
  // het kentekenbewijs (voertuig.documenten-compleet).
  'VERKOOPFACTUUR',
  'VRIJWARINGSBEWIJS',
  'KENTEKENBEWIJS',
  // batch-71 profiel-in-beeld (FO-538) - gegenereerde of teruggezette profielschets.
  'PROFIELSCHETS',
] as const
export type DocumentType = (typeof DOCUMENT_TYPE)[number]

/** Levenscyclus-status (FO-064 sectie 13). */
export const DOCUMENT_STATUS = [
  'ACTIEF',
  'VERVANGEN',
  'VERLOPEN',
  'CONCEPT',
  'IN_BEOORDELING',
  'GEARCHIVEERD',
] as const
export type DocumentStatus = (typeof DOCUMENT_STATUS)[number]

/** Fijnmazige zichtbaarheid bovenop de scope-/gevoelige-type-gates (FO-064 sectie 9). */
export const DOCUMENT_ZICHTBAARHEID = [
  'PRIVE',
  'INTERN',
  'ROLLEN',
  'GEBRUIKERS',
  'GEKOPPELDE_MEDEWERKER',
  'GEKOPPELDE_KLANT',
  'HR_ONLY',
  'FINANCE_ONLY',
  'DIRECTIE_ONLY',
  'VERBORGEN',
] as const
export type DocumentZichtbaarheid = (typeof DOCUMENT_ZICHTBAARHEID)[number]

/** Polymorf koppel-doel-type (FO-064 sectie 4/15). */
export const DOCUMENT_ENTITEIT_TYPE = [
  'MEDEWERKER',
  'KLANT',
  'WERKBON',
  'FACTUUR',
  'INKOOPFACTUUR',
  'CURSUS',
  'CERTIFICAAT',
  'SKILLMATRIX',
  'KWALITEITSTAAK',
  'ONGEVAL',
  'LEAD',
  'KANDIDAAT',
  'VOERTUIG',
  'ENTITEIT',
  // batch-55 bruikleen-docs (FO-421) — uniek middel (VoorraadUniekItem.id; een
  // kar/koffer hangt aan zijn onderliggende uniek item).
  'VOORRAAD_UNIEK_ITEM',
] as const
export type DocumentEntiteitType = (typeof DOCUMENT_ENTITEIT_TYPE)[number]

/** Rollen (spiegelt Prisma UserRole) — voor zichtbaarheid ROLLEN + maprechten. */
export const DOCUMENT_ROL = [
  'MONTEUR',
  'TEAMLEIDER',
  'PLANNER',
  'HR',
  'SALARIS',
  'MANAGER',
  'ADMIN',
] as const
export type DocumentRol = (typeof DOCUMENT_ROL)[number]

// ─── Label constants (NL-talig voor UI) ───────────────────────────────────────

export const DOCUMENT_CATEGORIE_LABELS: Record<DocumentCategorie, string> = {
  CONTRACT:     'Contract',
  CERTIFICAAT:  'Certificaat',
  ID_BEWIJS:    'ID-bewijs',
  EVALUATIE:    'Evaluatie',
  OVEREENKOMST: 'Overeenkomst',
  HANDLEIDING:  'Handleiding',
  PROTOCOL:     'Protocol',
  OVERIG:       'Overig',
}

export const DOCUMENT_SCOPE_LABELS: Record<DocumentScope, string> = {
  EMPLOYEE:      'Medewerker',
  CLIENT:        'Klant',
  WERKBON:       'Werkbon',
  BEDRIJFSBREED: 'Bedrijfsbreed',
  KWALITEIT:     'Kwaliteit',
  COMMUNICATIE:  'Communicatie',
  VOERTUIG:      'Voertuig',
  ONGEVAL:       'Ongeval',
  LEAD:          'Acquisitie-lead',
  KANDIDAAT:     'Kandidaat',
  EXPORT:        'Export',
  ABONNEMENT:    'Abonnement',
  VOORRAAD_UNIEK_ITEM: 'Middel (gereedschap, telefoon, kar of koffer)',
}

export const DOCUMENT_AUDIT_ACTIE_LABELS: Record<DocumentAuditActie, string> = {
  GEUPLOAD:                'Geüpload',
  NIEUWE_VERSIE:           'Nieuwe versie',
  GEDOWNLOAD:              'Gedownload',
  VERWIJDERD:              'Verwijderd',
  TOEGANG_GEWIJZIGD:       'Toegang gewijzigd',
  CATEGORIE_GEWIJZIGD:     'Categorie gewijzigd',
  GEOPEND:                 'Geopend',
  RECHTEN_GEWIJZIGD:       'Rechten gewijzigd',
  ZICHTBAARHEID_GEWIJZIGD: 'Zichtbaarheid gewijzigd',
  GEDEELD_MEDEWERKER:      'Gedeeld met medewerker-app',
  GEDEELD_KLANT:           'Gedeeld met klantportaal',
  KOPPELING_GEWIJZIGD:     'Koppeling gewijzigd',
  LABEL_TOEGEVOEGD:        'Label toegevoegd',
  LABEL_VERWIJDERD:        'Label verwijderd',
  BRIEFPAPIER_VASTGELEGD:  'Briefpapier vastgelegd',
}

// Heet `BIBLIOTHEEK_…` omdat employee-documents.ts (legacy LEGITIMATIE/CONTRACT/
// OVERIG) de naam DOCUMENT_TYPE_LABELS al bezet.
export const BIBLIOTHEEK_DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  FACTUUR:               'Factuur',
  INKOOPFACTUUR:         'Inkoopfactuur',
  CREDITFACTUUR:         'Creditfactuur',
  WERKBON:               'Werkbon',
  CERTIFICAAT:           'Certificaat',
  DIPLOMA:               'Diploma',
  SKILLMATRIX:           'Skillmatrix',
  ARBEIDSOVEREENKOMST:   'Arbeidsovereenkomst',
  BRUIKLEENOVEREENKOMST: 'Bruikleenovereenkomst',
  WERKGEVERSVERKLARING:  'Werkgeversverklaring',
  LOONSTROOK:            'Loonstrook',
  JAAROPGAVE:            'Jaaropgave',
  DECLARATIEBON:         'Declaratiebon',
  OFFERTE:               'Offerte',
  KLANTCONTRACT:         'Klantcontract',
  VEILIGHEIDSDOCUMENT:   'Veiligheidsdocument',
  OPLEIDINGSDOCUMENT:    'Opleidingsdocument',
  HANDLEIDING:           'Handleiding',
  TEMPLATE:              'Template',
  ALGEMEEN_DOCUMENT:     'Algemeen document',
  SCHADEFORMULIER:       'Schadeformulier (aanrijdingsformulier)',
  ID_KAART:              'ID-kaart',
  PASPOORT:              'Paspoort',
  RIJBEWIJS:             'Rijbewijs',
  BANKPAS:               'Bankpas',
  GROENE_KAART:          'Groene kaart',
  INZETVERKLARING:       'Inzetverklaring voertuig',
  APK_RAPPORT:           'APK-rapport',
  VERKLARING_PRIVEGEBRUIK: 'Verklaring privegebruik auto',
  WERKPLAATSFACTUUR:     'Werkplaatsfactuur',
  VERKOOPFACTUUR:        'Verkoopfactuur voertuig',
  VRIJWARINGSBEWIJS:     'Vrijwaringsbewijs',
  KENTEKENBEWIJS:        'Kentekenbewijs',
  PROFIELSCHETS:         'Profielschets',
}

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  ACTIEF:        'Actief',
  VERVANGEN:     'Vervangen',
  VERLOPEN:      'Verlopen',
  CONCEPT:       'Concept',
  IN_BEOORDELING:'In beoordeling',
  GEARCHIVEERD:  'Gearchiveerd',
}

export const DOCUMENT_ZICHTBAARHEID_LABELS: Record<DocumentZichtbaarheid, string> = {
  PRIVE:                 'Privé (alleen uploader)',
  INTERN:                'Intern STREEP',
  ROLLEN:                'Specifieke rollen',
  GEBRUIKERS:            'Specifieke gebruikers',
  GEKOPPELDE_MEDEWERKER: 'Gekoppelde medewerker',
  GEKOPPELDE_KLANT:      'Gekoppelde klant',
  HR_ONLY:               'Alleen HR',
  FINANCE_ONLY:          'Alleen Finance',
  DIRECTIE_ONLY:         'Alleen Directie',
  VERBORGEN:             'Verborgen',
}

export const DOCUMENT_ENTITEIT_TYPE_LABELS: Record<DocumentEntiteitType, string> = {
  MEDEWERKER:     'Medewerker',
  KLANT:          'Klant',
  WERKBON:        'Werkbon',
  FACTUUR:        'Factuur',
  INKOOPFACTUUR:  'Inkoopfactuur',
  CURSUS:         'Cursus',
  CERTIFICAAT:    'Certificaat',
  SKILLMATRIX:    'Skillmatrix',
  KWALITEITSTAAK: 'Kwaliteitstaak',
  ONGEVAL:        'Ongeval',
  LEAD:           'Acquisitie-lead',
  KANDIDAAT:      'Kandidaat',
  VOERTUIG:       'Voertuig',
  ENTITEIT:       'BV / entiteit',
  VOORRAAD_UNIEK_ITEM: 'Middel (gereedschap, telefoon, kar of koffer)',
}

export const DOCUMENT_ROL_LABELS: Record<DocumentRol, string> = {
  MONTEUR:    'Monteur',
  TEAMLEIDER: 'Teamleider',
  PLANNER:    'Planner',
  HR:         'HR',
  SALARIS:    'Salaris',
  MANAGER:    'Manager',
  ADMIN:      'Admin',
}

/**
 * Standaardrechten per documenttype (FO-064 sectie 11). Toegepast bij upload/
 * generatie wanneer de uploader geen expliciete zichtbaarheid kiest; bevoegden
 * (admin/directie) kunnen achteraf overriden. Versmalt alleen: de bestaande
 * scope-/gevoelige-type-gates (loonstrook/WGV/verzuim) blijven bovendien gelden.
 *
 * - LOONSTROOK/JAAROPGAVE -> alleen de gekoppelde medewerker (+ de bestaande
 *   PAYROLL-gate). ARBEIDS-/BRUIKLEEN-/WGV -> HR (+ Directie). SKILLMATRIX ->
 *   HR/TL/Directie. Financieel (facturen/declaraties) -> Finance/Directie.
 *   Overig -> INTERN (backoffice met DOCUMENTEN-permissie).
 */
export const DOCUMENT_TYPE_DEFAULT_RECHTEN: Record<
  DocumentType,
  { zichtbaarheid: DocumentZichtbaarheid; rollen?: DocumentRol[] }
> = {
  FACTUUR:               { zichtbaarheid: 'FINANCE_ONLY' },
  INKOOPFACTUUR:         { zichtbaarheid: 'FINANCE_ONLY' },
  CREDITFACTUUR:         { zichtbaarheid: 'FINANCE_ONLY' },
  DECLARATIEBON:         { zichtbaarheid: 'FINANCE_ONLY' },
  LOONSTROOK:            { zichtbaarheid: 'GEKOPPELDE_MEDEWERKER' },
  JAAROPGAVE:            { zichtbaarheid: 'GEKOPPELDE_MEDEWERKER' },
  ARBEIDSOVEREENKOMST:   { zichtbaarheid: 'HR_ONLY' },
  BRUIKLEENOVEREENKOMST: { zichtbaarheid: 'HR_ONLY' },
  WERKGEVERSVERKLARING:  { zichtbaarheid: 'HR_ONLY' },
  SKILLMATRIX:           { zichtbaarheid: 'ROLLEN', rollen: ['HR', 'TEAMLEIDER', 'MANAGER', 'ADMIN'] },
  CERTIFICAAT:           { zichtbaarheid: 'INTERN' },
  DIPLOMA:               { zichtbaarheid: 'INTERN' },
  WERKBON:               { zichtbaarheid: 'INTERN' },
  OFFERTE:               { zichtbaarheid: 'INTERN' },
  KLANTCONTRACT:         { zichtbaarheid: 'INTERN' },
  VEILIGHEIDSDOCUMENT:   { zichtbaarheid: 'INTERN' },
  OPLEIDINGSDOCUMENT:    { zichtbaarheid: 'INTERN' },
  HANDLEIDING:           { zichtbaarheid: 'INTERN' },
  TEMPLATE:              { zichtbaarheid: 'INTERN' },
  ALGEMEEN_DOCUMENT:     { zichtbaarheid: 'INTERN' },
  // FO-326: de bestuurder mag zijn eigen formulier zien (bestuurder-vlag).
  SCHADEFORMULIER:       { zichtbaarheid: 'GEKOPPELDE_MEDEWERKER' },
  // Identiteits-/pasdocumenten: gevoelig persoonsgegeven -> alleen HR.
  ID_KAART:              { zichtbaarheid: 'HR_ONLY' },
  PASPOORT:              { zichtbaarheid: 'HR_ONLY' },
  RIJBEWIJS:             { zichtbaarheid: 'HR_ONLY' },
  BANKPAS:               { zichtbaarheid: 'HR_ONLY' },
  // Voertuigdocumenten: INTERN, maar de actieve bestuurder ziet ze wél op de
  // portal-pagina Mijn voertuig (zie PORTAL_VOERTUIG_DOCUMENTTYPEN hieronder).
  GROENE_KAART:          { zichtbaarheid: 'INTERN' },
  INZETVERKLARING:       { zichtbaarheid: 'INTERN' },
  // batch-65 apk-rapport (FO-478) - INTERN, niet GEKOPPELDE_MEDEWERKER (spec A3
  // / ADDENDA noemen die, CC.0b-afwijking): een voertuigdocument heeft geen
  // gekoppelde medewerker, dus GEKOPPELDE_MEDEWERKER zou het rapport juist
  // verbergen - voor de bestuurder (enforceZichtbaarheid) én voor iedereen
  // behalve ADMIN op de voertuig-Documenten-tab (buildZichtbaarheidWhere). De
  // bestuurder ziet het via de carve-out hieronder, net als de groene kaart.
  APK_RAPPORT:           { zichtbaarheid: 'INTERN' },
  // batch-67 (FO-501) - persoonsgebonden verklaring in het HRM-dossier.
  VERKLARING_PRIVEGEBRUIK: { zichtbaarheid: 'HR_ONLY' },
  // batch-68 werkplaatsfacturen (FO-341 §3.5) - INTERN: wagenparkbeheer ziet hem
  // op de voertuig-Documenten-tab (VOERTUIGEN-gate). NIET in de bestuurder-
  // allowlist hieronder: er staan bedragen op. Wagenparkbeheer kan hem per
  // document vrijgeven (zie PORTAL_VOERTUIG_VRIJGAVE_DOCUMENTTYPEN).
  WERKPLAATSFACTUUR:     { zichtbaarheid: 'INTERN' },
  // batch-71 workflow-sjablonen (FO-533 §D9) - INTERN: wagenparkbeheer op de voertuig-
  // Documenten-tab; een verkoopfactuur draagt bedragen en hoort niet bij de bestuurder.
  VERKOOPFACTUUR:        { zichtbaarheid: 'INTERN' },
  VRIJWARINGSBEWIJS:     { zichtbaarheid: 'INTERN' },
  KENTEKENBEWIJS:        { zichtbaarheid: 'INTERN' },
  // batch-71 profiel-in-beeld (FO-538 §5) - bekijken/genereren is MEDEWERKERS.canView, en
  // dat is precies wat HR_ONLY toetst. Pas bij delen wordt hij medewerker-zichtbaar (A4).
  PROFIELSCHETS:         { zichtbaarheid: 'HR_ONLY' },
}

/**
 * Voertuigpapieren die de ACTIEVE bestuurder in de portal mag zien (blok
 * "Voertuigpapieren" op Mijn voertuig + de bijbehorende download-carve-out in
 * de portal-scope-check). Bewust een smalle allowlist: dit zijn de papieren
 * die je onderweg nodig hebt (controle, aanrijding). Alle overige
 * VOERTUIG-documenten blijven backoffice-only.
 */
export const PORTAL_VOERTUIG_DOCUMENTTYPEN = [
  'GROENE_KAART',
  'INZETVERKLARING',
  'SCHADEFORMULIER',
  // batch-65 apk-rapport (FO-478) - handig bij controle onderweg. De portal-
  // lijst toont alleen het LAATSTE APK-rapport (GET /voertuigen/me/documenten).
  'APK_RAPPORT',
] as const satisfies readonly DocumentType[]

/**
 * Zichtbaarheden waarbinnen de bestuurder-carve-out geldt; de besloten
 * niveaus (PRIVE/HR_ONLY/.../VERBORGEN) versmallen zoals altijd en blijven
 * dus ook voor de bestuurder dicht.
 */
export const PORTAL_VOERTUIG_ZICHTBAARHEDEN = [
  // batch-65 apk-rapport (FO-478) - het APK-rapport staat standaard op INTERN
  // (DOCUMENT_TYPE_DEFAULT_RECHTEN) en valt dus binnen deze carve-out.
  'INTERN',
  'GEKOPPELDE_MEDEWERKER',
] as const satisfies readonly DocumentZichtbaarheid[]

/**
 * batch-68 werkplaatsfacturen (FO-341 §3.5) - voertuigdocumenten die de
 * bestuurder NIET standaard ziet, maar wel nadat wagenparkbeheer dat ene
 * document heeft vrijgegeven (`gedeeldMetMedewerkerApp`). De werkplaatsfactuur
 * draagt bedragen; vrijgeven is een bewuste keuze per document.
 */
export const PORTAL_VOERTUIG_VRIJGAVE_DOCUMENTTYPEN = [
  'WERKPLAATSFACTUUR',
] as const satisfies readonly DocumentType[]

/**
 * Mag de actieve bestuurder dit voertuigdocument zien (los van de koppeling aan
 * zijn voertuig, die de aanroeper toetst)? De vaste allowlist, of een
 * vrijgave-type dat per document is vrijgegeven; altijd binnen de niet-besloten
 * zichtbaarheden. Eén regel voor de portal-lijst en de download-check.
 */
export function magBestuurderVoertuigDocumentZien(doc: {
  documenttype: string | null
  zichtbaarheid: string
  gedeeldMetMedewerkerApp: boolean
}): boolean {
  if (!doc.documenttype) return false
  if (!(PORTAL_VOERTUIG_ZICHTBAARHEDEN as readonly string[]).includes(doc.zichtbaarheid)) return false
  if ((PORTAL_VOERTUIG_DOCUMENTTYPEN as readonly string[]).includes(doc.documenttype)) return true
  return (
    (PORTAL_VOERTUIG_VRIJGAVE_DOCUMENTTYPEN as readonly string[]).includes(doc.documenttype) &&
    doc.gedeeldMetMedewerkerApp
  )
}

// ─── batch-45 documenten (FO-383) — zichtbaarheid-maximum bij upload ─────────

/**
 * Publieks-breedte per zichtbaarheid (hoger = breder publiek). Alleen bedoeld
 * voor de FO-383-maximum-regel bij upload: een gekozen (batch-)zichtbaarheid
 * mag de smallere typestandaard van een documenttype nooit VERBREDEN. De
 * onderlinge volgorde van de smalle niveaus is indicatief (HR vs Finance is een
 * ander publiek, geen sub-/superset) - de regel klemt daarom uitsluitend de
 * brede INTERN-keuze af, nooit een laterale smalle keuze.
 */
export const ZICHTBAARHEID_BREEDTE: Record<DocumentZichtbaarheid, number> = {
  INTERN:                100,
  GEKOPPELDE_KLANT:       60,
  ROLLEN:                 50,
  HR_ONLY:                40,
  FINANCE_ONLY:           40,
  DIRECTIE_ONLY:          30,
  GEBRUIKERS:             25,
  GEKOPPELDE_MEDEWERKER:  20,
  PRIVE:                  10,
  VERBORGEN:               0,
}

export interface ZichtbaarheidMaximumResultaat {
  zichtbaarheid: DocumentZichtbaarheid
  zichtbaarheidRollen: DocumentRol[]
  /** true = de smallere typestandaard heeft de gekozen zichtbaarheid vervangen. */
  typestandaardWint: boolean
}

/**
 * FO-383 — maximum-regel: de gekozen upload-zichtbaarheid kan een smallere
 * typestandaard nooit verbreden. Concreet klemt alleen de brede `INTERN`-keuze
 * af naar de typestandaard (bv. ARBEIDSOVEREENKOMST -> HR_ONLY); smalle keuzes
 * (HR_ONLY, PRIVE, ...) blijven een bewuste versmalling/laterale keuze en
 * winnen zoals voorheen. Gedeeld door de upload-UI (rij-uitleg) en de API
 * (server-side vangnet in `resolveUploadRechten`).
 *
 * Uitzondering (FO-385-samenhang): een typestandaard `GEKOPPELDE_MEDEWERKER`
 * klemt NIET - de "smalheid" daarvan is portal-blootstelling aan de
 * medewerker, geen staff-vertrouwelijkheid. Een bewuste INTERN-keuze is daar
 * juist de privacy-vriendelijke "nee, (nog) niet zichtbaar maken"-kant
 * (spec 88 T4: nee = intern, koppeling blijft).
 */
export function pasZichtbaarheidMaximumToe(
  gekozen: DocumentZichtbaarheid,
  documenttype: DocumentType | null | undefined,
): ZichtbaarheidMaximumResultaat {
  const standaard = documenttype ? DOCUMENT_TYPE_DEFAULT_RECHTEN[documenttype] : null
  if (
    standaard &&
    gekozen === 'INTERN' &&
    standaard.zichtbaarheid !== 'GEKOPPELDE_MEDEWERKER' &&
    ZICHTBAARHEID_BREEDTE[standaard.zichtbaarheid] < ZICHTBAARHEID_BREEDTE[gekozen]
  ) {
    return {
      zichtbaarheid: standaard.zichtbaarheid,
      zichtbaarheidRollen: standaard.rollen ?? [],
      typestandaardWint: true,
    }
  }
  return { zichtbaarheid: gekozen, zichtbaarheidRollen: [], typestandaardWint: false }
}

// ─── DTOs voor API responses ──────────────────────────────────────────────────

/** FO-077 — een label-chip op een document (uit de actieve koppelingen). */
export interface DocumentLabelChipDTO {
  code: string
  label: string
  /** Optioneel hex (zonder #) voor de chip-kleur. */
  kleur: string | null
  /** Herkomst van de koppeling: automatisch afgeleid of handmatig toegekend. */
  herkomst: 'AUTO' | 'HANDMATIG'
}

export interface DocumentScopeRefDTO {
  /** ID van het onderliggende object (employeeId / clientId / werkbonId), of null voor BEDRIJFSBREED. */
  id: string | null
  /** Display-label zoals 'Jan Jansen' / 'ASML B.V.' / 'WB-2026-001234'. Null voor BEDRIJFSBREED. */
  label: string | null
}

/**
 * batch-41 gesprekken (FO-360) — omgekeerde verwijzing document → gespreksverslag.
 * Gevuld wanneer dit document het GEGENEREERDE briefpapier-verslag van een
 * HR-gesprek is (`Gespreksverslag.documentId`). De UI stuurt hierop:
 *   - CONCEPT → alle ingangen linken naar de verslag-editor
 *     (`/hrm/gesprek-verslag/{gesprekId}`); "nieuwe versie uploaden" is
 *     geblokkeerd (de editor is de bron, een upload eroverheen = dataverlies).
 *   - DEFINITIEF/VRIJGEGEVEN → de documentweergave is de bevroren bestemming;
 *     de upload-blokkade blijft (her-genereren loopt altijd via de editor).
 */
export interface DocumentGespreksverslagRefDTO {
  /** HrGesprek-id — deeplink naar de verslag-editor. */
  gesprekId: string
  /**
   * Afgeleide verslag-status (VRIJGEGEVEN wint van DEFINITIEF wint van CONCEPT).
   * FO-378: HERZIENING = "Nieuwe versie maken" gestart (terug in bewerking).
   */
  status: 'CONCEPT' | 'DEFINITIEF' | 'VRIJGEGEVEN' | 'HERZIENING'
}

/**
 * UI-georiënteerde shape voor de documenten-lijst. We exposeren `scopeLabel`
 * als platte string + `huidigeVersieNr` voor mensvriendelijke weergave; deze
 * uitbreidingen zijn UI-driven (§A2).
 */
/** batch-68 (review I12) - de ondertekenstand van een document in een lijst. */
export interface DocumentLijstOndertekening {
  getekend: number
  /** Alle actieve vakken (FO-345 §3.3: "Getekend" = alle vakken). */
  totaal: number
  statusTekst: string
}

export interface DocumentListRowDTO {
  id: string
  titel: string
  beschrijving: string | null
  categorie: DocumentCategorie
  scope: DocumentScope
  /** Display-label van de scope-target (Jan Jansen / ASML B.V. / WB-2026-001234), of null voor BEDRIJFSBREED. */
  scopeLabel: string | null
  /** Hoogste versionNr in de versions[]-set. */
  huidigeVersieNr: number
  /** File-grootte van de laatste versie in bytes. */
  fileSize: number
  /** Mime-type van de laatste versie. */
  mimeType: string
  /** Display-naam van de uploader (User.name ?? email). */
  uploadedByName: string | null
  /** ISO datetime van Document.updatedAt (laatste mutatie). */
  updatedAt: string
  /** Tags-lijst (max 20). */
  tags: string[]
  /** Medewerker-app spec §8 — handtekening verplicht voor scope EMPLOYEE/BEDRIJFSBREED. */
  requiresSignature: boolean
  /** Deadline voor ondertekening; null wanneer geen deadline gezet of niet relevant. */
  signatureDeadline: string | null
  /** True als de huidige (portal-)user dit document al heeft ondertekend. Backoffice-list: altijd false. */
  signedByMe: boolean
  /**
   * batch-68 handtekening (FO-345 §3.3, review I12) - de stand van de
   * ondertekenaarslijst ("Getekend", "Deels getekend (1/2)", "Nog niet
   * getekend"). null of afwezig = geen lijst (dan tekent de medewerker zelf,
   * zie requiresSignature).
   */
  ondertekening?: DocumentLijstOndertekening | null
  /** FO-126 (batch-24) — lichte leesbevestiging-plicht (naast requiresSignature). */
  requiresAcknowledgement: boolean
  /** FO-126 — deadline voor de leesbevestiging; null als geen deadline of niet relevant. */
  acknowledgementDeadline: string | null
  /** FO-126 — true als de huidige (portal-)user dit document al heeft bevestigd te hebben gelezen. Backoffice-list: altijd false. */
  acknowledgedByMe: boolean
  /** FO-051 — export-type (slug) voor scope=EXPORT; null voor andere scopes. */
  exportType: string | null
  /** FO-051 — exportdatum (Document.createdAt, ISO) voor scope=EXPORT; null anders. */
  exportDatum: string | null
  /** FO-064 — bibliotheek-metadata. */
  documenttype: DocumentType | null
  documentStatus: DocumentStatus
  zichtbaarheid: DocumentZichtbaarheid
  gedeeldMetMedewerkerApp: boolean
  gedeeldMetKlantportaal: boolean
  /**
   * batch-56 klantenportaal (FO-413 D6) — ziet de klant dit document echt?
   * Afgeleid uit `Document.klantportaalVrijgegeven` én de categorie-instelling
   * van de klant, dus precies wat de klant-leesroute filtert. `null` = niet van
   * toepassing (geen CLIENT-document).
   */
  klantportaalZichtbaar: boolean | null
  /** Aantal koppelingen (meervoudig) op dit bestand. */
  koppelingAantal: number
  /** FO-077 — gestructureerde labels (auto + handmatig) als terugvind-as. */
  labels: DocumentLabelChipDTO[]
  /** batch-41 gesprekken (FO-360) — gegenereerd gespreksverslag? Dan de editor-ref. */
  gespreksverslag: DocumentGespreksverslagRefDTO | null
}

export interface DocumentVersionDTO {
  id: string
  versionNr: number
  fileName: string
  fileSize: number
  mimeType: string
  checksum: string | null
  uploadedBy: string
  uploadedByName: string | null
  uploadedAt: string
  /** FO-064 — reden van deze versie (waarom vervangen); null voor legacy/initiële versies. */
  reden: string | null
  /**
   * FO-415 (batch-57) — deze versie is het BUITEN de app getekende exemplaar dat
   * als nieuwe versie van hetzelfde document is geüpload (spec §6.1). Zichtbaar
   * in de versietabel, zodat op het document zelf te zien is welke versie de
   * getekende is.
   */
  getekendUpload: boolean
  /** Opgegeven tekendatum (ISO-datum) bij een getekende upload, anders null. */
  getekendOp: string | null
  /**
   * FO-372 — id van het bovenliggende document. Samen met {@link versionNr} de
   * bouwstenen waarmee de client zelf de same-origin download-proxy bouwt
   * (`/api/documenten/:documentId/versies/:versionNr/download`). De API geeft
   * bewust géén navigeerbaar `downloadUrl` meer terug: een directe link naar het
   * API-origin draagt geen Bearer-token en faalt altijd met UNAUTHORIZED.
   */
  documentId: string
}

export interface DocumentAuditDTO {
  id: string
  actie: DocumentAuditActie
  actor: string
  actorName: string | null
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

/** FO-064 — één koppeling (entiteit-type + id + display-label). */
export interface DocumentKoppelingDTO {
  id: string
  entiteitType: DocumentEntiteitType
  entiteitId: string
  /** Display-naam van de entiteit (medewerker/klant/werkbon-nummer), of null als niet resolvebaar. */
  label: string | null
}

export interface DocumentDetailDTO {
  /**
   * FO-459 (batch-62) - welk briefpapier elke gegenereerde versie kreeg (nieuwste
   * eerst); leeg bij geuploade documenten.
   */
  briefpapier?: import('./briefpapier').DocumentBriefpapierGebruikDTO[]
  /**
   * FO-459 / FO-378 - kan dit document opnieuw gegenereerd worden met een ander
   * briefpapier (nieuwe versie)? Null = geen sjabloon-document (eigen flow).
   */
  briefpapierHergeneratie?: import('./briefpapier').DocumentBriefpapierHergeneratieDTO | null
  id: string
  titel: string
  categorie: DocumentCategorie
  scope: DocumentScope
  scopeRef: DocumentScopeRefDTO
  beschrijving: string | null
  tags: string[]
  uploadedBy: string
  uploadedByName: string | null
  uploadedAt: string
  versions: DocumentVersionDTO[]
  audits: DocumentAuditDTO[]
  createdAt: string
  updatedAt: string
  // FO-064 — bibliotheek-metadata + fijnmazige rechten + koppelingen.
  documenttype: DocumentType | null
  documentStatus: DocumentStatus
  zichtbaarheid: DocumentZichtbaarheid
  zichtbaarheidRollen: DocumentRol[]
  /** User-ids met expliciete toegang (zichtbaarheid GEBRUIKERS) + hun display-naam. */
  zichtbaarheidGebruikers: Array<{ id: string; naam: string | null }>
  module: string | null
  gedeeldMetMedewerkerApp: boolean
  gedeeldMetKlantportaal: boolean
  // ── batch-56 klantenportaal (FO-413 D6) — vrijgave-poort naar het klantportaal ──
  /**
   * `true` = vrijgegeven en dus zichtbaar voor de klant; `false` = expliciet
   * ingetrokken; `null` = volgt de categorie-instelling van de klant
   * (standaard: NIET zichtbaar).
   */
  klantportaalVrijgegeven: boolean | null
  klantportaalVrijgaveOp: string | null
  klantportaalIngetrokkenOp: string | null
  /** batch-60 (FO-439b K1) — reden bij een intrekking; null als niet ingetrokken. */
  klantportaalVerbergReden: string | null
  koppelingen: DocumentKoppelingDTO[]
  /** True als de huidige gebruiker de fijnmazige rechten/zichtbaarheid mag wijzigen (admin/directie/canApprove). */
  magRechtenBeheren: boolean
  /** FO-077 — gestructureerde labels (auto + handmatig) op dit document. */
  labels: DocumentLabelChipDTO[]
  // ── Medewerker-app §8 + FO-126 (batch-24) — compliance-vlaggen ──────────────
  /** Handtekening-vereiste + deadline (voor de compliance-sectie). */
  requiresSignature: boolean
  signatureDeadline: string | null
  /** FO-126 — lichte leesbevestiging-vereiste + deadline (naast de handtekening). De
   * volledige "wie wel/niet"-compliance komt via GET /documenten/:id/acknowledgements. */
  requiresAcknowledgement: boolean
  acknowledgementDeadline: string | null
  /** batch-41 gesprekken (FO-360) — gegenereerd gespreksverslag? Dan de editor-ref. */
  gespreksverslag: DocumentGespreksverslagRefDTO | null
  /**
   * FO-378 (batch-44) — het versiebeleid van dit document (VRIJ/VIA_FLOW/NOOIT)
   * + de melding/verwijzing voor de UI. De frontend rendert hiermee de upload-
   * sectie (VRIJ), de flow-verwijzing (VIA_FLOW) of de bronmodule-verwijzing
   * (NOOIT) - nooit meer een doodlopend "definitief - kan niet wijzigen".
   */
  versiebeleid: DocumentVersiebeleidDTO
  /**
   * batch-45 (FO-384) — status van de zichtbare handtekening-verwerking:
   * openstaande (nog niet in de PDF verwerkte) handtekeningen mét de
   * opnieuw-actie, en de FO-378-waarschuwing wanneer er een nieuwere,
   * nog niet ondertekende versie bestaat. Null buiten de render-scope
   * (gespreksverslag/RI&E: die flows beheren hun eigen weergave).
   */
  handtekeningRender: DocumentHandtekeningRenderDTO | null
}

/**
 * batch-45 (FO-384) — één rij in de beheerlijst "getekend maar nog niet
 * zichtbaar" (GET /documenten/handtekeningen/onverwerkt): een geregistreerde
 * handtekening zonder verwerkte (getekende) PDF-versie.
 */
export interface OnverwerkteHandtekeningRowDTO {
  documentId: string
  documentTitel: string
  signatureId: string
  bron: 'MEDEWERKER' | 'KLANT'
  naam: string | null
  signedAt: string
  /** Versie waarop getekend is (null = actuele versie/legacy). */
  versionNr: number | null
  /** Laatste render-fout (null = nog nooit geprobeerd, bv. van vóór FO-384). */
  renderFout: string | null
}

/** batch-45 (FO-384) — resultaat van de her-render-actie per document. */
export interface VerwerkHandtekeningenResultDTO {
  verwerkt: number
  mislukt: Array<{ signatureId: string; reden: string }>
}

/** batch-45 (FO-384) — handtekening-verwerkstatus voor de document-detail. */
export interface DocumentHandtekeningRenderDTO {
  /** Handtekeningen die nog niet zichtbaar in de PDF verwerkt zijn. */
  onverwerkt: Array<{
    signatureId: string
    bron: 'MEDEWERKER' | 'KLANT'
    naam: string | null
    signedAt: string
    /** Laatste render-fout (null = nog niet geprobeerd). */
    renderFout: string | null
  }>
  /** FO-378-samenhang: er bestaat een nieuwere versie zonder handtekening. */
  nieuwereOngetekendeVersie: boolean
}

/** FO-378 — versiebeleid-resolutie zoals de detail-DTO die meelevert. */
export interface DocumentVersiebeleidDTO {
  klasse: 'VRIJ' | 'VIA_FLOW' | 'NOOIT'
  flow: string | null
  bronModule: string | null
  melding: { titel: string; tekst: string; actieLabel: string | null } | null
  /** Bouwstenen voor de flow-link (ids); de client bouwt zelf de href. */
  referentie: Record<string, string> | null
}

/** FO-126 (batch-24) — één rij in het compliance-overzicht "wie wel/niet" (per medewerker). */
export interface DocumentComplianceRowDTO {
  employeeId: string
  employeeName: string
  /** ISO datetime van de leesbevestiging; null = nog niet bevestigd. */
  acknowledgedAt: string | null
  /** ISO datetime van de handtekening; null = nog niet getekend. */
  signedAt: string | null
  /** True als deze medewerker te laat is t.o.v. de acknowledgementDeadline (nog niet bevestigd én deadline verstreken). */
  teLaat: boolean
}

/**
 * FO-126 (batch-24) — compliance-overzicht van één document: doelgroep (N),
 * X-bevestigd/X-getekend, per-medewerker-status én de actieve handtekeningen +
 * leesbevestigingen. Bouwt het bestaande gat dat ook handtekeningen geen
 * overzicht hadden meteen dicht.
 */
export interface DocumentComplianceDTO {
  documentId: string
  titel: string
  scope: DocumentScope
  requiresAcknowledgement: boolean
  acknowledgementDeadline: string | null
  requiresSignature: boolean
  signatureDeadline: string | null
  /** True wanneer de acknowledgementDeadline is verstreken. */
  acknowledgementDeadlinePassed: boolean
  /** Doelgroep-grootte (N): aantal medewerkers dat het document moet bevestigen/tekenen. */
  targetCount: number
  acknowledgedCount: number
  signedCount: number
  /** Per medewerker in de doelgroep: bevestigd/getekend ja/nee + datum. */
  rows: DocumentComplianceRowDTO[]
}

/** FO-064 — een node in de virtuele mappenboom (map of bestand). */
export interface DocumentMapNodeDTO {
  /** 'map' = virtuele folder (verder navigeerbaar); 'bestand' = een document. */
  type: 'map' | 'bestand'
  /** Weergavenaam van de node. */
  naam: string
  /** Canoniek pad-segment-pad voor een map (gebruikt om dieper te navigeren). */
  pad: string | null
  /** Voor type 'bestand': het document-id (anders null). */
  documentId: string | null
  /** Voor type 'map': aantal bestanden eronder (rechten-gefilterd). Voor bestand: null. */
  aantal: number | null
  /** Voor type 'bestand': documenttype-label + huidige versie. */
  documenttype: DocumentType | null
  huidigeVersieNr: number | null
}

/** FO-064 — maprecht-rij (DocumentMap) voor het beheer-overzicht. */
export interface DocumentMapRechtDTO {
  id: string
  pad: string
  zichtbaarheid: DocumentZichtbaarheid
  rollen: DocumentRol[]
  gebruikers: Array<{ id: string; naam: string | null }>
  erftVanBovenliggend: boolean
  updatedAt: string
}

/** FO-064 — bibliotheek-dashboard (sectie 16). */
export interface DocumentDashboardDTO {
  totaalActief: number
  perType: Array<{ documenttype: DocumentType | null; aantal: number }>
  laatstToegevoegd: DocumentListRowDTO[]
  /** Documenten zonder enige koppeling (opschonen). */
  zonderKoppeling: DocumentListRowDTO[]
  /** Documenten met status VERLOPEN of binnenkort verlopend (handtekening-deadline). */
  verlopend: DocumentListRowDTO[]
}

// ─── Zod-schemas voor request bodies ──────────────────────────────────────────

// ID-refs accepteren cuid, legacy-UUID én seed-ids — prod-data bevat alle
// drie de formaten (zie id-validators.ts). Een strikte `.cuid()` weigerde
// uploads/filters voor medewerkers/klanten/werkbonnen met een UUID-id.
const idRef = idParam('id')

/**
 * Schema voor POST /documenten (multipart). Velden komen uit form-data buiten
 * het `file`-veld.
 */
export const createDocumentSchema = z
  .object({
    titel:        z.string().trim().min(1).max(200),
    categorie:    z.enum(DOCUMENT_CATEGORIE),
    scope:        z.enum(DOCUMENT_SCOPE),
    employeeId:   idRef.optional(),
    clientId:     idRef.optional(),
    werkbonId:    idRef.optional(),
    /** F6 Kwaliteit — FK naar KwaliteitsTaak bij scope=KWALITEIT. */
    kwaliteitsTaakId: idRef.optional(),
    /** Batch-7 — FK naar TargetingLead bij scope=LEAD (acquisitie). */
    targetingLeadId: idRef.optional(),
    /** Batch-7 — FK naar Kandidaat bij scope=KANDIDAAT (recruitment). */
    kandidaatId: idRef.optional(),
    /** FO-314 (batch-34) — FK naar Abonnement bij scope=ABONNEMENT. */
    abonnementId: idRef.optional(),
    /**
     * batch-39 bibliotheek (FO-333) — voertuig-koppeling bij scope=VOERTUIG.
     * Document heeft geen voertuigId-kolom; dit veld stuurt de DocumentKoppeling
     * (entiteitType VOERTUIG) zodat de upload direct op de voertuig-documenten-
     * tab verschijnt. Optioneel (bestaande VOERTUIG-uploads zonder koppeling
     * blijven geldig); het gedeelde upload-formulier stuurt hem altijd mee.
     */
    voertuigId: idRef.optional(),
    /**
     * batch-55 bruikleen-docs (FO-421) — uniek-middel-koppeling bij scope
     * VOORRAAD_UNIEK_ITEM (gereedschap, telefoon, kar, koffer). Zelfde
     * mechaniek als voertuigId: geen FK-kolom op Document, het veld stuurt de
     * DocumentKoppeling (entiteitType VOORRAAD_UNIEK_ITEM) zodat de upload
     * direct op het item-detail verschijnt. Verplicht bij die scope (er is
     * geen legacy zonder koppeling).
     */
    voorraadUniekItemId: idRef.optional(),
    /**
     * FO-375 (batch-43) — per-rij medewerker-KOPPELING bij een bibliotheek-upload.
     * Géén scope-FK (dit veld staat bewust NIET in de "BEDRIJFSBREED mag geen
     * FK"-check hieronder): het wordt een DocumentKoppeling MEDEWERKER, zodat een
     * bedrijfsbreed document toch in het dossier van de medewerker verschijnt met
     * de standaard-zichtbaarheid van het documenttype. Optioneel; leeg = geen koppeling.
     */
    gekoppeldeMedewerkerId: idRef.optional(),
    /** Vrij sub-type binnen de scope (bv. 'CV' voor kandidaat-uploads). */
    bijlageType: z.string().trim().max(40).optional(),
    beschrijving: z.string().trim().max(2000).optional(),
    tags:         z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    // FO-064 (batch-19) — bibliotheek-metadata bij upload. zichtbaarheid is
    // optioneel: ontbreekt 'ie, dan past de server de standaardrechten van het
    // documenttype toe (DOCUMENT_TYPE_DEFAULT_RECHTEN).
    documenttype: z.enum(DOCUMENT_TYPE).optional(),
    zichtbaarheid: z.enum(DOCUMENT_ZICHTBAARHEID).optional(),
    zichtbaarheidRollen: z.array(z.enum(DOCUMENT_ROL)).max(7).optional(),
    /**
     * batch-45 (FO-383) — bewuste keuze "óók voor alle medewerkers in de portal"
     * bij een BEDRIJFSBREED-upload: zet de bestaande deel-vlag
     * `gedeeldMetMedewerkerApp` direct bij het uploaden (mét GEDEELD_MEDEWERKER-
     * audit). Zonder deze vlag is een verse bibliotheek-upload alleen
     * backoffice-zichtbaar (de nieuwe intern-default). Backoffice-only.
     */
    deelMetMedewerkerApp: z
      .union([z.boolean(), z.literal('true'), z.literal('false'), z.literal('on')])
      .transform((v) => v === true || v === 'true' || v === 'on')
      .optional(),
    /**
     * batch-45 (FO-385) — bevestiging dat deze upload zichtbaar wordt voor de
     * gekoppelde medewerker in de portal. Server-side verplicht wanneer de
     * effectieve zichtbaarheid `GEKOPPELDE_MEDEWERKER` is op een handmatig
     * backoffice-uploadpad (kernregel: nooit stilzwijgend zichtbaar); de
     * upload-UI stelt daarvoor de batch-bevestigingsvraag. Portal-selfservice
     * (medewerker-eigen) valt onder de allowlist en vereist geen bevestiging.
     */
    zichtbaarheidBevestigd: z
      .union([z.boolean(), z.literal('true'), z.literal('false'), z.literal('on')])
      .transform((v) => v === true || v === 'true' || v === 'on')
      .optional(),
    module: z.string().trim().max(80).optional(),
    /** Medewerker-app spec §8.7 — handtekening-verplichte upload. */
    requiresSignature: z
      .union([z.boolean(), z.literal('true'), z.literal('false'), z.literal('on')])
      .transform((v) => v === true || v === 'true' || v === 'on')
      .optional(),
    /** ISO-date string (YYYY-MM-DD) of leeg/onbestaand. Wordt op de server naar einde-van-dag (NL) genormaliseerd. */
    signatureDeadline: z.string().date('Kies een geldige datum (jjjj-mm-dd)').optional(),
    /** FO-126 (batch-24) — lichte leesbevestiging-verplichte upload (naast requiresSignature). */
    requiresAcknowledgement: z
      .union([z.boolean(), z.literal('true'), z.literal('false'), z.literal('on')])
      .transform((v) => v === true || v === 'true' || v === 'on')
      .optional(),
    /** ISO-date (YYYY-MM-DD) of leeg/onbestaand. Server normaliseert naar einde-van-dag. */
    acknowledgementDeadline: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .superRefine((val, ctx) => {
    // Discriminated FK matching scope.
    if (val.scope === 'EMPLOYEE' && !val.employeeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['employeeId'],
        message: 'employeeId is verplicht voor scope EMPLOYEE',
      })
    }
    if (val.scope === 'CLIENT' && !val.clientId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clientId'],
        message: 'clientId is verplicht voor scope CLIENT',
      })
    }
    if (val.scope === 'WERKBON' && !val.werkbonId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['werkbonId'],
        message: 'werkbonId is verplicht voor scope WERKBON',
      })
    }
    if (val.scope === 'KWALITEIT' && !val.kwaliteitsTaakId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['kwaliteitsTaakId'],
        message: 'kwaliteitsTaakId is verplicht voor scope KWALITEIT',
      })
    }
    if (val.scope === 'LEAD' && !val.targetingLeadId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetingLeadId'],
        message: 'targetingLeadId is verplicht voor scope LEAD',
      })
    }
    if (val.scope === 'KANDIDAAT' && !val.kandidaatId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['kandidaatId'],
        message: 'kandidaatId is verplicht voor scope KANDIDAAT',
      })
    }
    if (val.scope === 'ABONNEMENT' && !val.abonnementId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['abonnementId'],
        message: 'abonnementId is verplicht voor scope ABONNEMENT',
      })
    }
    // batch-39 (FO-333) — voertuigId hoort alleen bij scope VOERTUIG.
    if (val.voertuigId && val.scope !== 'VOERTUIG') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['voertuigId'],
        message: 'voertuigId is alleen toegestaan bij scope VOERTUIG',
      })
    }
    // batch-55 (FO-421) — voorraadUniekItemId hoort bij (en is verplicht voor)
    // scope VOORRAAD_UNIEK_ITEM.
    if (val.voorraadUniekItemId && val.scope !== 'VOORRAAD_UNIEK_ITEM') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['voorraadUniekItemId'],
        message: 'voorraadUniekItemId is alleen toegestaan bij scope VOORRAAD_UNIEK_ITEM',
      })
    }
    if (val.scope === 'VOORRAAD_UNIEK_ITEM' && !val.voorraadUniekItemId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['voorraadUniekItemId'],
        message: 'voorraadUniekItemId is verplicht voor scope VOORRAAD_UNIEK_ITEM',
      })
    }
    if (val.scope === 'BEDRIJFSBREED') {
      if (val.employeeId || val.clientId || val.werkbonId || val.kwaliteitsTaakId || val.targetingLeadId || val.kandidaatId || val.abonnementId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scope'],
          message: 'BEDRIJFSBREED-scope mag geen FK hebben',
        })
      }
    }
  })

export const updateDocumentSchema = z.object({
  titel:        z.string().trim().min(1).max(200).optional(),
  categorie:    z.enum(DOCUMENT_CATEGORIE).optional(),
  beschrijving: z.string().trim().max(2000).nullable().optional(),
  tags:         z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  // FO-064 — documenttype/status/module via de gewone metadata-edit (canEdit).
  // Zichtbaarheid/rechten lopen apart via updateDocumentRechtenSchema (bevoegden).
  documenttype:   z.enum(DOCUMENT_TYPE).nullable().optional(),
  documentStatus: z.enum(DOCUMENT_STATUS).optional(),
  module:         z.string().trim().max(80).nullable().optional(),
})

/** FO-bestandstype-filter — afgeleid uit mimeType-groepen. */
export const DOCUMENT_BESTANDSTYPE = ['PDF', 'EXCEL', 'WORD', 'POWERPOINT', 'AFBEELDING', 'OVERIG'] as const
export type DocumentBestandstype = (typeof DOCUMENT_BESTANDSTYPE)[number]

export const listDocumentenQuerySchema = z.object({
  scope:            z.enum(DOCUMENT_SCOPE).optional(),
  categorie:        z.enum(DOCUMENT_CATEGORIE).optional(),
  employeeId:       idRef.optional(),
  clientId:         idRef.optional(),
  werkbonId:        idRef.optional(),
  kwaliteitsTaakId: idRef.optional(),
  targetingLeadId:  idRef.optional(),
  kandidaatId:      idRef.optional(),
  abonnementId:     idRef.optional(),
  search:           z.string().trim().max(100).optional(),
  tag:              z.string().trim().max(40).optional(),
  // FO-064 (batch-19) — bibliotheek-filters (sectie 8).
  documenttype:     z.enum(DOCUMENT_TYPE).optional(),
  documentStatus:   z.enum(DOCUMENT_STATUS).optional(),
  zichtbaarheid:    z.enum(DOCUMENT_ZICHTBAARHEID).optional(),
  module:           z.string().trim().max(80).optional(),
  bestandstype:     z.enum(DOCUMENT_BESTANDSTYPE).optional(),
  /** Alleen documenten zonder enige koppeling (opschoon-weergave). */
  zonderKoppeling:  queryBoolean().optional(),
  // FO-077 (batch-20) — label-filter (zoekbare multi-select). Codes komma-gescheiden
  // of als herhaalde param; labelMode = AND (alle labels) of OR (één van, default).
  labels:           z.preprocess(
                      (v) =>
                        typeof v === 'string'
                          ? v.split(',').map((s) => s.trim()).filter(Boolean)
                          : v,
                      z.array(z.string().trim().max(60)).max(20).optional(),
                    ),
  labelMode:        z.enum(['and', 'or']).optional(),
  // FO-051 (batch-16) — Exports-weergave: filter op export-type, uitvoerder en
  // exportdatum-bereik (alleen zinvol bij scope=EXPORT, maar serverside generiek
  // toegepast).
  exportType:       z.string().trim().max(120).optional(),
  uitvoerderId:     idRef.optional(),
  datumVan:         z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  datumTot:         z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page:             z.coerce.number().int().positive().default(1),
  // Cap verhoogd naar 2000 zodat de bibliotheek-lijst de volledige (gefilterde)
  // set in één keer kan ophalen en client-side globaal kan sorteren + pagineren.
  pageSize:         z.coerce.number().int().positive().max(2000).default(20),
})

// ─── FO-077 (batch-20) — bibliotheek-label-schemas ────────────────────────────

export interface DocumentLabelDTO {
  id: string
  code: string
  label: string
  omschrijving: string | null
  kleur: string | null
  icoon: string | null
  actief: boolean
  sortOrder: number
  /** Aantal actieve koppelingen (voor de legenda-tellingen). */
  aantalKoppelingen: number
}

export const createDocumentLabelSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Geef een code op')
    .max(60)
    .regex(/^[A-Z0-9_]+$/, 'Alleen hoofdletters, cijfers en underscores'),
  label: z.string().trim().min(1, 'Geef een naam op').max(120),
  omschrijving: z.string().trim().max(500).optional().nullable(),
  kleur: z
    .string()
    .regex(/^[0-9A-Fa-f]{6}$/, 'Hex zonder # (6 tekens)')
    .optional()
    .nullable(),
  icoon: z.string().trim().max(60).optional().nullable(),
  sortOrder: z.coerce.number().int().min(0).max(9999).optional(),
})
export type CreateDocumentLabelInput = z.infer<typeof createDocumentLabelSchema>

export const updateDocumentLabelSchema = z.object({
  label: z.string().trim().min(1).max(120).optional(),
  omschrijving: z.string().trim().max(500).optional().nullable(),
  kleur: z
    .string()
    .regex(/^[0-9A-Fa-f]{6}$/, 'Hex zonder # (6 tekens)')
    .optional()
    .nullable(),
  icoon: z.string().trim().max(60).optional().nullable(),
  actief: z.boolean().optional(),
  sortOrder: z.coerce.number().int().min(0).max(9999).optional(),
})
export type UpdateDocumentLabelInput = z.infer<typeof updateDocumentLabelSchema>

/** POST /documenten/:id/labels — handmatig een label toevoegen (op code). */
export const documentLabelToevoegenSchema = z.object({
  code: z.string().trim().min(1).max(60),
})
export type DocumentLabelToevoegenInput = z.infer<typeof documentLabelToevoegenSchema>

// ─── FO-064 (batch-19) — bibliotheek-actie-schemas ────────────────────────────

/** Fijnmazige rechten/zichtbaarheid wijzigen (bevoegden: admin/directie/canApprove). */
export const updateDocumentRechtenSchema = z
  .object({
    zichtbaarheid: z.enum(DOCUMENT_ZICHTBAARHEID),
    zichtbaarheidRollen: z.array(z.enum(DOCUMENT_ROL)).max(7).default([]),
    /** User-ids (resolved client-side uit de medewerker-picker). */
    zichtbaarheidGebruikerIds: z.array(idRef).max(100).default([]),
    /**
     * batch-45 (FO-385) — expliciete bevestiging dat het document zichtbaar
     * wordt voor de gekoppelde medewerker in de portal. Server-side verplicht
     * bij een wijziging NAAR `GEKOPPELDE_MEDEWERKER` (kernregel: nooit
     * stilzwijgend zichtbaar); de UI stelt daarvoor de bevestigingsvraag.
     */
    zichtbaarheidBevestigd: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.zichtbaarheid === 'ROLLEN' && val.zichtbaarheidRollen.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['zichtbaarheidRollen'],
        message: 'Kies minstens één rol bij zichtbaarheid "Specifieke rollen"',
      })
    }
    if (val.zichtbaarheid === 'GEBRUIKERS' && val.zichtbaarheidGebruikerIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['zichtbaarheidGebruikerIds'],
        message: 'Kies minstens één gebruiker bij zichtbaarheid "Specifieke gebruikers"',
      })
    }
  })

/** Delen naar medewerker-app / klantportaal (altijd gelogd). */
export const shareDocumentSchema = z
  .object({
    gedeeldMetMedewerkerApp: z.boolean().optional(),
    gedeeldMetKlantportaal: z.boolean().optional(),
  })
  .refine(
    (v) => v.gedeeldMetMedewerkerApp !== undefined || v.gedeeldMetKlantportaal !== undefined,
    { message: 'Geef minstens één deel-doel op' },
  )

/** Een koppeling toevoegen aan een bestand. */
export const addKoppelingSchema = z.object({
  entiteitType: z.enum(DOCUMENT_ENTITEIT_TYPE),
  entiteitId: idRef,
  /**
   * batch-45 (FO-385) — bevestiging bij het koppelen van een MEDEWERKER aan
   * een document dat al medewerker-zichtbaar is (`GEKOPPELDE_MEDEWERKER`):
   * daarmee wordt het document direct zichtbaar in diens portal. Server-side
   * verplicht in dat geval; de UI stelt de bevestigingsvraag.
   */
  zichtbaarheidBevestigd: z.boolean().optional(),
})

/** Maprechten op een virtueel pad upserten (admin/directie). */
export const upsertDocumentMapSchema = z.object({
  pad: z.string().trim().min(1).max(400),
  zichtbaarheid: z.enum(DOCUMENT_ZICHTBAARHEID),
  rollen: z.array(z.enum(DOCUMENT_ROL)).max(7).default([]),
  gebruikerIds: z.array(idRef).max(100).default([]),
  erftVanBovenliggend: z.boolean().default(true),
})

/** Hard verwijderen (admin/directie) - reden verplicht. */
export const hardDeleteDocumentSchema = z.object({
  reden: z.string().trim().min(3).max(500),
})

/** Virtuele-mappen-resolver query. */
export const mappenQuerySchema = z.object({
  pad: z.string().trim().max(400).optional(),
})

export type CreateDocumentInput = z.infer<typeof createDocumentSchema>
export type UpdateDocumentInput = z.infer<typeof updateDocumentSchema>
export type ListDocumentenQuery = z.infer<typeof listDocumentenQuerySchema>
